import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, MessageCircle, Pencil, RotateCcw, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';

type Lang = 'pt' | 'en' | 'es';
type Messages = Record<Lang, string>;

const LANGS: { code: Lang; labelKey: string }[] = [
  { code: 'pt', labelKey: 'admin.langPt' },
  { code: 'en', labelKey: 'admin.langEn' },
  { code: 'es', labelKey: 'admin.langEs' },
];

const EMPTY: Messages = { pt: '', en: '', es: '' };

const toLang = (lng: string | undefined): Lang => {
  const base = (lng || 'pt').slice(0, 2);
  return base === 'en' || base === 'es' ? base : 'pt';
};

// Link público que já abre o formulário (ver pages/Testimonials.tsx, ?enviar=1)
const buildClientLink = () => {
  const base = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '/' ? process.env.PUBLIC_URL.replace(/\/$/, '') : '';
  return `${window.location.origin}${base}/depoimentos?enviar=1`;
};

// Mensagem que a equipe copia para pedir depoimento ao cliente. Texto personalizado fica no
// backend (Settings.testimonialRequest, por idioma); vazio = texto padrão das traduções.
const RequestMessageCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const ta = (key: string, opts?: Record<string, unknown>) => t(`testimonialsAdmin.${key}`, opts);
  const notifications = useNotifications();
  const [saved, setSaved] = useState<Messages>(EMPTY);
  const [draft, setDraft] = useState<Messages>(EMPTY);
  const [lang, setLang] = useState<Lang>(toLang(i18n.language));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const defaultText = (l: Lang) =>
    t('testimonialsAdmin.clientMessage', { lng: l, link: '{link}', interpolation: { escapeValue: false } });
  const textFor = (msgs: Messages, l: Lang) => msgs[l] || defaultText(l);

  useEffect(() => {
    axios.get('/testimonials/request-message')
      .then((res) => {
        const data = { ...EMPTY, ...res.data };
        setSaved(data);
        setDraft(data);
      })
      .catch(() => { /* sem mensagem salva: usa o texto padrão */ });
  }, []);

  const copy = async () => {
    const text = textFor(editing ? draft : saved, lang);
    const link = buildClientLink();
    const message = text.includes('{link}') ? text.split('{link}').join(link) : `${text.trim()}\n\n${link}`;
    try {
      await navigator.clipboard.writeText(message);
      notifications.showSuccess(ta('linkCopied'), ta('copyLinkHint'));
    } catch {
      notifications.error.network();
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Texto igual ao padrão é salvo vazio, para continuar acompanhando o padrão das traduções
      const payload = Object.fromEntries(
        LANGS.map(({ code }) => [code, draft[code].trim() === defaultText(code).trim() ? '' : draft[code]])
      );
      const res = await axios.put('/testimonials/request-message', payload);
      const data = { ...EMPTY, ...res.data };
      setSaved(data);
      setDraft(data);
      setEditing(false);
      notifications.showSuccess(ta('requestSaved'), '');
    } catch (err: any) {
      notifications.error.settingsUpdate(err.response?.data?.message || ta('errAction'));
    } finally {
      setSaving(false);
    }
  };

  // Mesmo fluxo dos outros campos traduzíveis do admin: português → inglês/espanhol, revisar e salvar
  const translate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post('/testimonials/request-message/translate', { message: textFor(draft, 'pt') });
      setDraft({ ...draft, en: res.data.en, es: res.data.es });
      notifications.showSuccess(ta('requestTranslated'), ta('requestTranslatedHint'));
    } catch (err: any) {
      notifications.showError(ta('requestTranslateError'), err.response?.data?.message || ta('errAction'));
    } finally {
      setTranslating(false);
    }
  };

  const current = textFor(draft, lang);
  const isCustom = !!saved[lang];

  return (
    <div className="mt-6 card">
      <div className="flex items-start gap-3">
        <MessageCircle className="w-5 h-5 text-primary-600 shrink-0 mt-1" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className="text-lg font-semibold text-gray-900">{ta('requestTitle')}</h2>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isCustom ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}>
              {isCustom ? ta('requestCustom') : ta('requestDefault')}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{ta('requestDesc')}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label={ta('requestCopyLang')}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="tab"
              aria-selected={lang === l.code}
              onClick={() => setLang(l.code)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                lang === l.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(l.labelKey)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { if (editing) setDraft(saved); setEditing(!editing); }}
            className="btn-outline inline-flex items-center gap-2"
          >
            {editing ? <X className="w-4 h-4 shrink-0" /> : <Pencil className="w-4 h-4 shrink-0" />}
            {editing ? ta('requestClose') : ta('requestEdit')}
          </button>
          <button type="button" onClick={copy} className="btn-primary inline-flex items-center gap-2">
            <Copy className="w-4 h-4 shrink-0" />
            {ta('copyLink')}
          </button>
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={current}
            onChange={(e) => setDraft({ ...draft, [lang]: e.target.value })}
            rows={9}
            maxLength={2000}
            className="input-field w-full font-normal"
          />
          {!current.includes('{link}') && (
            <p className="text-sm text-amber-700">{ta('requestNoLink')}</p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <button
                type="button"
                onClick={() => setDraft({ ...draft, [lang]: '' })}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <RotateCcw className="w-4 h-4 shrink-0" />
                {ta('requestRestore')}
              </button>
              {lang !== 'pt' && (
                <button
                  type="button"
                  onClick={translate}
                  disabled={translating}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                >
                  <Sparkles className="w-4 h-4 shrink-0" />
                  {translating ? ta('requestTranslating') : ta('requestTranslate')}
                </button>
              )}
            </div>
            <button type="button" onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
              {saving ? ta('requestSaving') : ta('requestSave')}
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-700 whitespace-pre-line bg-gray-50 rounded-lg p-3 border border-gray-100">
          {current}
        </p>
      )}
    </div>
  );
};

export default RequestMessageCard;
